import { useEffect, useCallback, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import BlissBasicLogo from '../components/bliss/BlissBasicLogo'
import BlissBasicProductsTab from '../components/admin/bliss/BlissBasicProductsTab'
import OwnerWorkspacePreview from '../components/beach-bumz/OwnerWorkspacePreview'
import SupplierCatalog from '../components/beach-bumz/SupplierCatalog'
import { BLISS_BASIC_PUBLIC_ORIGIN } from '../config/blissBasicHost'
import { isBlissBasicPartnerTenant } from '../utils/blissBasicBackOfficeGate'
import './BlissBasicBackOffice.css'
import BeachBumzDashboard from '../components/beach-bumz/BeachBumzDashboard'
import { supabase } from '../lib/supabase'
import { createTeamRequest } from '../components/beach-bumz/teamRequest'

/**
 * Beach Bumz fulfillment workspace; existing catalog studio for other partners.
 * Platform staff extras only with ?staff=1 on the URL.
 */
export default function BlissBasicBackOffice() {
  const { signOut } = useAuth()
  const { tenantName, tenantSlug, settings, isPlatformAdmin, isLoading, role } = useTenant()
  const [searchParams] = useSearchParams()
  const staffMode = searchParams.get('staff') === '1'
  const teamRequest = useMemo(() => createTeamRequest(async () => (await supabase.auth.getSession()).data.session?.access_token || null), [])
  const vendorRequest = useCallback(async (body?: Record<string, unknown>) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new Error('Please sign in again.')
    const response = await fetch('/api/beach-bumz-orders', {
      method: body ? 'PATCH' : 'GET',
      headers: { Authorization: `Bearer ${session.access_token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Unable to load vendor orders.')
    return data
  }, [])

  const isPartner = isBlissBasicPartnerTenant(
    tenantSlug,
    tenantName,
    settings as Record<string, unknown>,
  )

  useEffect(() => {
    // The pearl stage itself comes from applyThemeToDocument, which treats
    // /basic-admin as a back-office route; this class only adds the studio's
    // serif type scale on top of it.
    document.documentElement.classList.add('bliss-basic-backoffice-root')
    return () => {
      document.documentElement.classList.remove('bliss-basic-backoffice-root')
    }
  }, [])

  const landingUrl =
    typeof window !== 'undefined' && window.location.hostname.includes('basic.')
      ? '/'
      : BLISS_BASIC_PUBLIC_ORIGIN

  const catalogOwnerName = (() => {
    const fromSettings =
      typeof settings.owner_name === 'string' ? settings.owner_name.trim() : ''
    if (fromSettings) return fromSettings
    if (isPartner && (tenantSlug === 'beach-bumz' || (tenantName ?? '').toLowerCase().includes('beach bumz'))) {
      return 'Josh Steintz'
    }
    return ''
  })()

  if (isLoading) return <div className="bbv-shell"><main className="bbv-main" role="status">Loading your workspace…</main></div>
  if (!isPlatformAdmin && (!isPartner || !['owner', 'admin'].includes(role || ''))) {
    return <div className="bliss-basic-backoffice"><div className="bbbo-wrong-tenant"><h1>Workspace access required</h1><p>Sign in with your Beach Bumz owner or admin account.</p><button onClick={() => void signOut()}>Sign out</button></div></div>
  }
  if (tenantSlug === 'beach-bumz' || isPlatformAdmin) {
    if (isPlatformAdmin) return <OwnerWorkspacePreview teamRequest={teamRequest} request={vendorRequest} onSignOut={() => void signOut()} partnerName={catalogOwnerName || 'Josh Steintz'} />
    return <BeachBumzDashboard teamRequest={teamRequest} request={vendorRequest} onSignOut={() => void signOut()} ownerName={isPlatformAdmin ? 'Bliss Owner' : catalogOwnerName || 'Josh Steintz'} catalog={<SupplierCatalog />} />
  }

  if (!isLoading && isPlatformAdmin && !isPartner) {
    return (
      <div className="bliss-basic-backoffice">
        <div className="bbbo-wrong-tenant">
          <BlissBasicLogo variant="nav" className="bbbo-logo" />
          <h1>Preview Josh&apos;s studio</h1>
          <p>
            Your active tenant is not a Bliss Basic partner. In{' '}
            <a href="https://admin.blissbunkbeds.com/admin">admin</a>, use the tenant menu to select{' '}
            <strong>Beach Bumz</strong>, then open{' '}
            <a href="https://basic.blissbunkbeds.com/basic-admin">basic.blissbunkbeds.com/basic-admin</a>.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bliss-basic-backoffice">
      <header className="bbbo-header">
        <div className="bbbo-header-start">
          <BlissBasicLogo variant="nav" className="bbbo-logo" />
          <div className="bbbo-header-meta">
            <span className="bbbo-tenant">{tenantName ?? 'Your catalog'}</span>
            {catalogOwnerName ? (
              <span className="bbbo-user">Owner: {catalogOwnerName}</span>
            ) : null}
          </div>
        </div>
        <div className="bbbo-header-actions">
          <a className="bbbo-link" href={landingUrl} target="_blank" rel="noopener noreferrer">
            View landing
          </a>
          {isPlatformAdmin && staffMode && (
            <a
              className="bbbo-link bbbo-link--staff"
              href="https://admin.blissbunkbeds.com/admin"
              target="_blank"
              rel="noopener noreferrer"
            >
              Staff: Master Control
            </a>
          )}
          <button type="button" className="bbbo-signout" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </header>

      <main className="bbbo-main">
        <BlissBasicProductsTab />
      </main>

      <footer className="bbbo-footer">
        <Link to="/" className="bbbo-link">
          ← Bliss Basic
        </Link>
      </footer>
    </div>
  )
}


